// The 14 EU allergens (ALG-01, ALG-02, ALG-06), each a three-way choice of "—" (none), "Può contenere" or
// "Contiene", under a "Nessun allergene" confirmation. The confirmation records that the item was checked and has none,
// so it is off while any allergen is set; setting an allergen ends it, as the server does. Every change sends the
// allergens and the confirmation together, so the two never disagree.
import { ALLERGENS } from "../allergens";
import type { AllergenId, AllergenLevel, AllergenMap } from "../types";
import { CheckRow, FieldError, Segmented, type SegmentOption } from "./drawerFields";

type Choice = AllergenLevel | "none";
const CHOICES: SegmentOption<Choice>[] = [
  { value: "none", label: "—", ariaLabel: "Nessuno" },
  // As on the list's badges: Contiene filled, Può contenere outlined.
  { value: "may_contain", label: "Può contenere", activeClass: "bg-white text-gray-900 shadow-sm ring-1 ring-inset ring-gray-400" },
  { value: "contains", label: "Contiene", activeClass: "bg-gray-800 text-white shadow-sm" },
];

export interface AllergenPatch {
  allergens: AllergenMap;
  noAllergensConfirmed: boolean;
}

/** The patch a choice makes: an allergen set ends the confirmation, one cleared leaves it as it was. */
export function allergenChoice(current: AllergenPatch, id: AllergenId, choice: Choice): AllergenPatch {
  const allergens: AllergenMap = { ...current.allergens };
  if (choice === "none") delete allergens[id];
  else allergens[id] = choice;
  return { allergens, noAllergensConfirmed: choice === "none" ? current.noAllergensConfirmed : false };
}

interface Props {
  item: AllergenPatch;
  disabled: boolean;
  error?: string | null;
  onSave: (patch: AllergenPatch) => void;
}

export function AllergenEditor({ item, disabled, error, onSave }: Props) {
  const anySet = Object.keys(item.allergens).length > 0;
  return (
    <div>
      <CheckRow
        checked={item.noAllergensConfirmed && !anySet}
        disabled={disabled || anySet}
        onChange={(none) => onSave({ allergens: item.allergens, noAllergensConfirmed: none })}
        label="Nessun allergene"
        hint={anySet ? "Togli prima gli allergeni" : item.noAllergensConfirmed ? undefined : "Controllato, non ne contiene nessuno"}
      />
      <FieldError message={error} />
      <ul className="mt-1 divide-y divide-gray-100">
        {ALLERGENS.map(({ id, name }) => {
          const level = item.allergens[id];
          return (
            <li key={id} className="flex items-center gap-2 py-[5px]">
              <span className={level ? "min-w-0 flex-1 text-[13px] font-medium text-gray-900" : "min-w-0 flex-1 text-[13px] text-gray-600"}>
                {name}
              </span>
              <Segmented
                options={CHOICES}
                value={level ?? "none"}
                disabled={disabled}
                onChange={(choice) => onSave(allergenChoice(item, id, choice))}
                ariaLabel={name}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}
